import { useState } from 'react';

import { faFlag as faFlagRegular } from '@fortawesome/free-regular-svg-icons';
import {
  faArrowUpFromBracket,
  faFilePdf,
  faFlag,
  faShare,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';

import { useReferralPdfGenerator } from '@/modules/referrals/components/ReferralPdfDownload/ReferralPdfGenerator';
import { useReferralStatus } from '@/react-query/useReferralInfo';

interface ReferralStatusIconProps {
  unifiedAppointmentId?: string;
  onOpenReferModal: () => void;
  onFlagReferral?: () => void;
  onUnmarkReferral?: () => void;
  onResubmitReferral?: () => void;
}

export const ReferralStatusIcon = ({
  unifiedAppointmentId,
  onOpenReferModal,
  onFlagReferral,
  onUnmarkReferral,
  onResubmitReferral,
}: ReferralStatusIconProps) => {
  const { t } = useLingui();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { data: referralStatusData } = useReferralStatus(
    unifiedAppointmentId || '',
    {
      enabled: !!unifiedAppointmentId,
    },
  );
  const referralStatus = referralStatusData?.status;
  const hasRequestData = referralStatusData?.hasRequestData;

  const { generatePdf, isGenerating, isLoading, hasData } =
    useReferralPdfGenerator({
      unifiedAppointmentId:
        hasRequestData && unifiedAppointmentId
          ? unifiedAppointmentId
          : undefined,
    });

  if (!unifiedAppointmentId) {
    return null;
  }

  const getIconAndColor = () => {
    switch (referralStatus) {
      case 'submitted':
        return {
          icon: faShare,
          tooltip: t`Referral sent`,
        };
      case 'needs_referral':
        return {
          icon: faShare,
          tooltip: t`Referral flagged`,
        };
      case 'failed':
        return {
          icon: faShare,
          tooltip: t`Referral failed - needs resubmission`,
        };
      default:
        return {
          icon: faShare,
          tooltip: t`Send Referral`,
        };
    }
  };

  const { icon, tooltip } = getIconAndColor();

  const getDropdownItems = () => {
    const items = [];

    // Show "Send Referral" only if no referral has been submitted and status is not 'failed'
    if (referralStatus !== 'submitted' && referralStatus !== 'failed') {
      items.push({
        label: t`Send Referral`,
        onClick: onOpenReferModal,
        icon: faArrowUpFromBracket,
      });
    }

    // Show "Send Referral" only if status is 'failed'
    if (referralStatus === 'failed') {
      items.push({
        label: t`Send Referral`,
        onClick: onResubmitReferral,
        icon: faArrowUpFromBracket,
      });
    }

    // Show "Flag a referral" only if referral is not submitted and status is not already 'needs_referral'
    if (
      referralStatus !== 'submitted' &&
      referralStatus !== 'needs_referral' &&
      referralStatus !== 'failed'
    ) {
      items.push({
        label: t`Referral Needed`,
        onClick: onFlagReferral,
        icon: faFlagRegular,
      });
    }

    // Show "Unmark a referral" only if status is 'needs_referral'
    if (referralStatus === 'needs_referral') {
      items.push({
        label: t`Referral Not Needed`,
        onClick: onUnmarkReferral,
        icon: faFlag,
      });
    }

    return items;
  };

  const dropdownItems = getDropdownItems();

  // Only return null if there are no dropdown items AND no request data to show
  if (dropdownItems.length === 0 && !hasRequestData) return null;

  const handleDownloadPdf = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isGenerating || isLoading || !hasData) return;

    await generatePdf();
    // Close dropdown after PDF generation is complete
    setIsDropdownOpen(false);
  };

  return (
    <div className="vai-flex vai-flex-row vai-justify-end vai-w-full vai-text-text-accent vai-font-thin">
      <Tooltip eager>
        <TooltipTrigger asChild>
          <DropdownMenu
            open={isDropdownOpen}
            onOpenChange={setIsDropdownOpen}
          >
            <DropdownMenuTrigger asChild>
              <button className="vai-flex vai-items-center vai-justify-center vai-w-fit vai-h-fit vai-m-0 vai-bg-transparent vai-border-none vai-cursor-pointer hover:vai-bg-grey-200 vai-rounded-md vai-px-2 vai-py-0 vai-transition-colors vai-duration-200 focus:vai-outline-none focus:vai-ring-0">
                <FontAwesomeIcon
                  icon={icon}
                  className={`vai-transition-all vai-duration-200 vai-text-gray-600`}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {dropdownItems.map((item, index) => (
                <DropdownMenuItem
                  key={index}
                  onClick={item.onClick}
                  className="vai-text-sm vai-w-fit vai-h-fit vai-cursor-pointer vai-flex vai-items-center vai-gap-2"
                >
                  <FontAwesomeIcon
                    icon={item.icon}
                    className="vai-w-3 vai-h-3"
                  />
                  {item.label}
                </DropdownMenuItem>
              ))}
              {hasRequestData && (
                <div
                  onClick={handleDownloadPdf}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleDownloadPdf(e as any);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-disabled={isGenerating || isLoading || !hasData}
                  className={`vai-text-sm vai-w-fit vai-h-fit vai-cursor-pointer vai-flex vai-items-center vai-gap-2 vai-px-2 vai-py-1.5 vai-rounded-sm vai-transition-colors ${
                    isGenerating || isLoading || !hasData
                      ? 'vai-text-gray-400 vai-cursor-not-allowed'
                      : 'hover:vai-bg-gray-100 focus:vai-bg-gray-100 focus:vai-outline-none'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={isGenerating ? faSpinner : faFilePdf}
                    className={`vai-w-3 vai-h-3 ${
                      isGenerating ? 'vai-animate-spin' : ''
                    }`}
                  />
                  {isGenerating || isLoading
                    ? t`Preparing PDF...`
                    : t`Download PDF`}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </div>
  );
};
